import { z } from 'zod'
import { orderRefractionSchema } from './refraction'
import type { VariableValues } from '../types/strapi/order'
import { VariableValuesSchema } from '../types/strapi/order'
import { PromotionsResultSchema } from '../types/strapi/discount-rules'

export const OrderSchema = z.object({
  localId: z.string(),
  inventory_no: z.string().optional(),
  color: z.string(),
  tinting: z.string(),
  distance: z.string(),
  frame: z.string(),
  leg: z.string().nullish(),
  note: z.string(),
  l_fh: z.number(),
  r_fh: z.number(),
  lens: z.string(),
  lifestyle: z.string().optional(),
  // What is this?
  mirror: z.string().optional(),
  corridor: z.string().optional(),
  refraction: orderRefractionSchema,
  strategy_ref: z.string(),
  frame_from_stock: z.boolean(),
  first_id: z.number().optional(),
  premium_insurance: z.boolean().optional(),
  // FIXME: Remove optional
  variable_values: VariableValuesSchema.optional(),
})

export type Order = z.infer<typeof OrderSchema>

export const OrderConfigSchema = z.object({
  customer_id: z.number(),
  orders: z.array(OrderSchema).min(1),
  store_id: z.union([z.number(), z.string()]),
  staff_id: z.union([z.number(), z.string()]),
  invoice_id: z.number().nullish(),
})

export const ErpOrderSchema = OrderSchema.extend({
  localId: z.undefined(),
  variable_values: z.undefined(),
})

export type ErpOrder = z.infer<typeof ErpOrderSchema>

export const ErpOrderConfigSchema = z.object({
  customer_id: z.number(),
  orders: z.array(ErpOrderSchema).min(1),
  store_id: z.union([z.number(), z.string()]),
  staff_id: z.union([z.number(), z.string()]),
  invoice_id: z.number().nullish(),
})

export const ErpOrderPatchSchema = z.object({
  order: ErpOrderSchema,
  store_id: z.union([z.number(), z.string()]),
  staff_id: z.union([z.number(), z.string()]),
  customer_id: z.number(),
  invoice_id: z.number().nullish(),
})

export type OrderConfig = z.infer<typeof OrderConfigSchema>

export type ErpOrderConfig = z.infer<typeof ErpOrderConfigSchema>

export type InternalOrderPayload = {
  localId: string
  variable_values?: VariableValues
  data: ErpOrder
}

export type ErpOrderConfigPayload = Omit<ErpOrderConfig, 'orders'> & {
  orders: InternalOrderPayload[]
}

export type ErpOrderPatch = z.infer<typeof ErpOrderPatchSchema>

export const ErpOrderResponseSchema = z.object({
  amount: z.number(),
  id: z.number(),
  name: z.string(),
})

export const ErpOrderConfigResponseSchema = z.object({
  orders: z.array(ErpOrderResponseSchema),
})

export const OrderResponseSchema = ErpOrderResponseSchema.extend({
  data: OrderSchema,
  final_price: z.number().optional(),
})

export const SubmitOrderSchema = z.object({
  order_ids: z.array(z.number()),
})

export type ErpOrderResponse = z.infer<typeof ErpOrderResponseSchema>

export const ErpOrderResponseLocalIdSchema = ErpOrderResponseSchema.extend({
  localId: z.string(),
})

export type ErpOrderResponseLocalId = z.infer<
  typeof ErpOrderResponseLocalIdSchema
>

export type ErpOrderConfigResponse = z.infer<
  typeof ErpOrderConfigResponseSchema
>

export type OrderResponse = z.infer<typeof OrderResponseSchema>
export type OrderConfigResponse = z.infer<typeof OrderConfigResponseSchema>

export const ErpApplyPointsInputSchema = z.object({
  order_ids: z.array(z.number()),
  points: z.number(),
})

export const ErpApplyReferrerInputSchema = z.object({
  order_ids: z.array(z.number()),
  // Customer ref
  referrer: z.string().nullable(),
})

export const ErpApplyVoucherInputSchema = z.object({
  order_ids: z.array(z.number()),
  code: z.string(),
})

export const ApplyReferrerBaseInputSchema = z.object({
  orders: z.array(OrderResponseSchema),
  referrer: z.string().nullable(),
})

export const ApplyPointsBaseInputSchema = z.object({
  orders: z.array(OrderResponseSchema),
  points: z.number(),
})

export const ApplyVoucherBaseInputSchema = z.object({
  orders: z.array(OrderResponseSchema),
  code: z.string().nullable(),
})

export type ErpApplyPointsInput = z.infer<typeof ErpApplyPointsInputSchema>
export type ErpApplyVoucherInput = z.infer<typeof ErpApplyVoucherInputSchema>
export type ErpApplyReferrerInput = z.infer<typeof ErpApplyReferrerInputSchema>

export type ApplyPointsInput = z.infer<typeof ApplyPointsBaseInputSchema>
export type ApplyVoucherInput = z.infer<typeof ApplyVoucherBaseInputSchema>
export type ApplyReferrerInput = z.infer<typeof ApplyReferrerBaseInputSchema>

export const ErpOrderDiscountedPriceSchema = z.object({
  amount: z.number(),
  id: z.number(),
})

export type ErpOrderDiscountedPrice = z.infer<
  typeof ErpOrderDiscountedPriceSchema
>

export const ErpDiscountSchema = z.object({
  discount: z.number(),
  description: z.string().optional(),
  orders: z.array(ErpOrderDiscountedPriceSchema).optional(),
})

export const ErpVoucherSchema = ErpDiscountSchema.extend({
  voucher_ids: z.array(z.number()),
})

export const ErpPointsSchema = ErpDiscountSchema.extend({
  point_ids: z.array(z.number()),
})

export const DiscountTypes = [
  'promotion',
  'credit',
  'coupon',
  'gift-card',
  'referral',
] as const

export const DiscountTypeSchema = z.enum(DiscountTypes)

export type DiscountType = z.infer<typeof DiscountTypeSchema>

export const isGiftCard = (code?: string) => {
  return code?.trim().toLowerCase()?.startsWith('gc')
}

export const TaggedDiscountInputSchema = z.union([
  z.object({
    id: z.string().optional(),
    mode: z.literal('credit'),
    input: ApplyPointsBaseInputSchema,
    // points_ids: z.array(z.number()),
  }),
  z.object({
    id: z.string().optional(),
    mode: z.enum(['coupon', 'gift-card', 'promotion']),
    input: ApplyVoucherBaseInputSchema,
    // voucher_ids: z.array(z.number()),
  }),
  z.object({
    id: z.string().optional(),
    mode: z.literal('referral'),
    input: ApplyReferrerBaseInputSchema,
  }),
])

export const CouponDiscountResponseSchema = ErpDiscountSchema.extend({
  mode: z.literal('coupon'),
  input: ApplyVoucherBaseInputSchema,
  voucher_ids: z.array(z.number()),
})

export const GiftCardDiscountResponseSchema = ErpDiscountSchema.extend({
  mode: z.literal('gift-card'),
  input: ApplyVoucherBaseInputSchema,
  voucher_ids: z.array(z.number()),
})

export const ReferralDiscountResponseSchema = ErpDiscountSchema.extend({
  mode: z.literal('referral'),
  input: ApplyReferrerBaseInputSchema,
  // Not sure if this is correct but we are getting the voucher ids from the erp
  voucher_ids: z.array(z.number()),
})

export const CreditDiscountResponseSchema = ErpDiscountSchema.extend({
  mode: z.literal('credit'),
  input: ApplyPointsBaseInputSchema,
  points_ids: z.array(z.number()),
})

export const PromotionDiscountResponseSchema = ErpDiscountSchema.extend({
  mode: z.literal('promotion'),
  input: ApplyVoucherBaseInputSchema,
  voucher_ids: z.array(z.number()),
})
export const DiscountResponseSchema = z.union([
  CouponDiscountResponseSchema,
  ReferralDiscountResponseSchema,
  CreditDiscountResponseSchema,
  GiftCardDiscountResponseSchema,
  PromotionDiscountResponseSchema,
])

export type CouponDiscountResponse = z.infer<
  typeof CouponDiscountResponseSchema
>
export type ReferralDiscountResponse = z.infer<
  typeof ReferralDiscountResponseSchema
>
export type CreditDiscountResponse = z.infer<
  typeof CreditDiscountResponseSchema
>
export type GiftCardDiscountResponse = z.infer<
  typeof GiftCardDiscountResponseSchema
>
export type PromotionDiscountResponse = z.infer<
  typeof PromotionDiscountResponseSchema
>
export type TaggedDiscountInput = z.infer<typeof TaggedDiscountInputSchema>

export const ApplyReferrerInputSchema = ApplyReferrerBaseInputSchema.extend({
  toRemove: TaggedDiscountInputSchema.nullable(),
})

export const ApplyPointsInputSchema = ApplyPointsBaseInputSchema.extend({
  toRemove: TaggedDiscountInputSchema.nullable(),
})

export const ApplyVoucherInputSchema = ApplyVoucherBaseInputSchema.extend({
  toRemove: TaggedDiscountInputSchema.nullable(),
})

export type DiscountResponse = z.infer<typeof DiscountResponseSchema>

export const ErpDiscountResponseSchema = z.object({
  code: z.number(),
  data: ErpDiscountSchema,
  error: z.string().optional(),
})

export const OrderConfigResponseSchema = z.object({
  orders: z.array(OrderResponseSchema),
  invoice_id: z.number().nullish(),
  isValid: z.boolean(),
  total: z.number(),
  promotions: PromotionsResultSchema.optional(),
})

export const OrderPatchSchema = z.object({
  customer_id: z.number(),
  orders: z
    .array(
      z.object({
        updated: z.boolean().nullish(),
        res: ErpOrderResponseSchema.nullable(),
        data: OrderSchema,
      })
    )
    .min(1),
  staff_id: z.union([z.number(), z.string()]),
  store_id: z.union([z.number(), z.string()]),
  discounts: z.array(DiscountResponseSchema).optional(),
  invoice_id: z.number().nullish(),
})

export const ErpVoucherResponseSchema = z.object({
  code: z.number(),
  data: ErpVoucherSchema,
  error: z.string().optional(),
})

export const ErpPointsResponseSchema = z.object({
  code: z.number(),
  data: ErpPointsSchema,
  error: z.string().optional(),
})

export type ErpDiscount = z.infer<typeof ErpDiscountSchema>

export type ErpVoucherDiscount = ErpDiscount & {
  voucher_ids: number[]
}

export type ErpPointsDiscount = ErpDiscount & {
  points_ids: number[]
}

export type ErpDiscountResponse = z.infer<typeof ErpDiscountResponseSchema>

export type ErpVoucherResponse = z.infer<typeof ErpVoucherResponseSchema>
export type ErpPointsResponse = z.infer<typeof ErpPointsResponseSchema>

export type StockSource = 'global' | 'local'

export type StockSettings = {
  inventory: string
  source: StockSource
  trace?: boolean
}
